import { useState } from 'react';
import { Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import type { KeyInsight } from '../types';
import { deltaColors } from '../constants';

export function InsightCard({ insight, onAskAI }: { insight: KeyInsight; onAskAI?: (q: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const colors = deltaColors[insight.deltaType];

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${colors.border}`,
        borderRadius: '10px',
        overflow: 'hidden',
        backgroundColor: 'var(--background)',
      }}
    >
      {/* Collapsed header — always visible */}
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          padding: '10px 16px',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
        }}
      >
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: colors.text, flexShrink: 0, width: '112px', lineHeight: '1.25', overflowWrap: 'anywhere' }}>
          {insight.delta}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--foreground)', marginBottom: '1px', lineHeight: '1.3' }}>
            {insight.title}
          </p>
          {!expanded && (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.35' }}>
              {insight.summary}
            </p>
          )}
        </div>
        <span style={{ color: 'var(--muted-foreground)', flexShrink: 0, marginLeft: '8px', marginTop: '1px', display: 'flex' }}>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: '0 18px 18px 18px', borderTop: '1px solid var(--border)' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.5', margin: '14px 0 12px' }}>
            {insight.summary}
          </p>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)', lineHeight: '1.65', marginBottom: '14px' }}>
            {insight.body}
          </p>

          {/* Metadata dots */}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
            {[
              `${insight.confidence}% confidence`,
              insight.sources,
              insight.timeAgo,
            ].map((meta, i) => (
              <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {i > 0 && <span style={{ color: 'var(--border)', fontSize: 'var(--text-sm)' }}>•</span>}
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>{meta}</span>
              </span>
            ))}
          </div>

          {/* AI Reasoning box */}
          <div style={{
            backgroundColor: '#f3f0fd',
            border: '1px solid rgba(126,95,224,0.2)',
            borderRadius: '8px',
            padding: '13px 15px',
            marginBottom: '14px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
              <Sparkles size={13} color="#7e5fe0" />
              <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: '#7e5fe0' }}>AI Reasoning:</span>
            </div>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)', lineHeight: '1.55' }}>
              {insight.aiReasoning}
            </p>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => onAskAI?.(`${insight.title}: ${insight.summary}`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                padding: '8px 16px',
                backgroundColor: 'transparent',
                border: '1px solid var(--spectral-violet)',
                borderRadius: '7px',
                fontSize: 'var(--text-sm)',
                fontWeight: 500,
                color: 'var(--spectral-violet)',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--spectral-violet)';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--spectral-violet)';
              }}
            >
              <Sparkles size={13} />
              Ask AI
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
